import type { Gender } from '../api/types';
import { formatNumber, useJourneyLocale } from '../i18n/journey';
import { mascotImagePath, type Tradition } from '../theme/tokens';

/** What Home's Today card is currently showing -- the greeting must never contradict it (the old
 * blocking popup promised "your practice is ready" even when Today was empty). */
export type GreetingTodayStatus = 'loading' | 'practice' | 'empty' | 'error';

interface MascotWelcomeProps {
  gender: Gender | null;
  displayName: string;
  currentStreak: number;
  primaryTradition: Tradition | null;
  todayStatus: GreetingTodayStatus;
  onDismiss: () => void;
}

/** A compact, optional, dismissible greeting from the companion the user picked (review fix
 * 2026-09-10): inline above Today rather than a blocking daily overlay, no call to action of its
 * own competing with Begin, and its line is derived from the actual Today state. */
export function MascotWelcome({ gender, displayName, currentStreak, primaryTradition, todayStatus, onDismiss }: MascotWelcomeProps) {
  const { t, lang } = useJourneyLocale();
  const image = mascotImagePath(primaryTradition, gender);
  const streakLine =
    currentStreak > 1 && todayStatus === 'practice'
      ? ` ${t('greeting.streakDay', { day: formatNumber(lang, currentStreak) })}`
      : '';

  return (
    <aside className="welcome-greeting" aria-label={t('greeting.ariaLabel')}>
      <img src={image} alt="" className="welcome-greeting-avatar" />
      <p className="welcome-greeting-text">
        <strong>{t('greeting.welcomeBack', { name: displayName })}</strong> {t(`greeting.today.${todayStatus}`)}
        {streakLine}
      </p>
      <button type="button" className="welcome-greeting-close" onClick={onDismiss} aria-label={t('greeting.hide')}>
        &times;
      </button>
    </aside>
  );
}
